import { collection } from '@/infra/collection'
import { type WorkspacePostingsApi } from '@/features/opportunities/api/workspace-postings-api'
import { Id } from '@/infra/id'
import { catchError, defer, EMPTY, type Observable } from 'rxjs'
import { mergeMap, filter, map, share } from 'rxjs/operators'
import { whileActive } from '@/utils/rx-util'
import { WorkspaceInvitation } from './WorkspaceInvitation'
import { mapWorkspaceInvitationResourceToProps } from './workspace-invitation-mappers'

/**
 * Loads and caches the invitations to apply to workspace postings.
 */
export class WorkspaceOpportunityInvitationStore {
  /**
   * Invitations collection.
   */
  readonly workspaceInvitations = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new WorkspaceInvitation(),
  })

  /**
   * Emits pending invitations. Used on first load.
   */
  private readonly _invitationsFromFetch$ = defer(async () =>
    this._api.invitations.list({
      limit: 100,
    }),
  ).pipe(
    mergeMap((resp) => resp.data),
    // Throwing would end the incoming stream for the session.
    catchError(() => EMPTY),
  )

  /**
   * Observable of incoming invitations.
   */
  readonly incomingInvitations$: Observable<WorkspaceInvitation>

  constructor(
    private readonly _api: WorkspacePostingsApi,
    private readonly _canViewOpportunities$: Observable<boolean>,
  ) {
    this.incomingInvitations$ = this._invitationsFromFetch$.pipe(
      whileActive(this._canViewOpportunities$),
      filter((x) => !this.workspaceInvitations.get(x.id)),
      map((x) =>
        this.workspaceInvitations.set(mapWorkspaceInvitationResourceToProps(x)),
      ),
      share(),
    )
  }

  /**
   * Accepts the invitation.
   *
   * @param invitationId
   */
  async acceptInvitation(invitationId: Id) {
    const invitation = this.workspaceInvitations.get(invitationId)
    if (!invitation) {
      return
    }
    await this._api.invitations.accept(Id.assert(invitationId)).then(() => {
      this.workspaceInvitations.remove(invitation)
    })
  }

  /**
   * Rejects the invitation.
   *
   * @param invitationId
   */
  async rejectInvitation(invitationId: Id) {
    const invitation = this.workspaceInvitations.get(invitationId)
    if (!invitation) {
      return
    }
    await this._api.invitations.reject(Id.assert(invitationId)).then(() => {
      this.workspaceInvitations.remove(invitation)
    })
  }
}
